import type { ToastOptions } from "@picklebet/ui";
import { QueueError, ResultError } from "utilities/message/message";

const QUEUE_MISSING_ERROR = "Application error when queuing message.";
const RESULT_MISSING_ERROR = "Application error when processing message.";

const API_STRINGS: Record<string, string> = {
  "insufficient-entry-funds":
    "You have insufficient funds to pay the entry fee, deposit to play!",
  "insufficient-withdrawal-funds":
    "You have insufficient funds in your withdrawable balance to complete the withdrawal.",
  "invalid-withdrawal-amount":
    "The amount you are trying to withdraw is invalid.",
  "user-verification-required":
    "Your identity must first be verified in order to withdraw funds. Please contact support.",
  "contest-not-found":
    "We were unable to find the specified contest in the database.",
  "contest-not-pending":
    "You can only perform this action on a contest that hasn't started yet!",
  "roster-exceeds-rosters-per-user":
    "You cannot submit any more rosters to this contest.",
  "roster-exceeds-rosters-per-contest":
    "This contest is full, please try another one.",
  "invalid-bet-amount": "You have exceeded your maximum bet amount.",
  "unsupported-currency":
    "Your residence currently prohibits entry into contests of this currency type",
  "threeds-enrollment-failed": "Your card is not enrolled in 3DS",
};

const parseMessageError = (error: QueueError | ResultError) => {
  const hasError = error.errors && error.errors.length > 0;

  if (hasError) {
    return error.errors.map((e) => e.message).join(", ");
  }

  const mappedError = error.code ? API_STRINGS[error.code] : undefined;

  if (mappedError) {
    return mappedError;
  }

  if (error.message) {
    return error.message;
  }

  if (error instanceof QueueError) {
    return QUEUE_MISSING_ERROR;
  }

  return RESULT_MISSING_ERROR;
};

/**
 * Check if the error object has validation errors
 * @param body
 */
export const hasErrors = ({ body }) =>
  (body && body.errors && body.errors.length > 0) || false;

/**
 * Extract validation errors from the error object
 * @param body
 */
export const extractMessages = ({ body }) => {
  return body.errors.reduce((messages, error) => {
    if (!messages[error.propertyPath]) {
      messages[error.propertyPath] = [];
    }
    messages[error.propertyPath].push(error.message);
    return messages;
  }, {});
};

/**
 * Extracts a single flat message per field
 * @param body
 * @return {*}
 */
export const extractMessagesFlat = ({ body }) => {
  return body.errors.reduce((messages, error) => {
    messages[error.propertyPath || error.messageTemplate] = error.message;
    return messages;
  }, {});
};

export function parseError(error: any): string {
  if (error instanceof QueueError || error instanceof ResultError) {
    return parseMessageError(error);
  }

  if (error instanceof Error) {
    return error.message;
  }

  console.warn(`Infrastructure error.`, error);
  return (
    error.message || "An error occurred. Please try again or come back later."
  );
}

export const convertErrorToToast = (
  error: any,
  options: ToastOptions = {},
): ToastOptions => {
  return {
    description: parseError(error),
    variant: "danger",
    ...options,
  };
};
